import { staticPageMetadata } from '../../lib/i18n/staticPageMetadata';
import type { ScrollViewInstance } from 'react-native';
import { View, Text, ScrollView, Platform, TouchableOpacity, KeyboardAvoidingView, Linking } from 'react-native';
import { Stack, Link, useLocalSearchParams, usePathname } from 'expo-router';
import { FontAwesome6 } from '@expo/vector-icons';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import AnnualBillingSwitch from '../../components/billing/AnnualBillingSwitch';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSession } from '../../lib/SessionContext';
import { useAuth } from '../../lib/AuthContext';
import MetaTags from '../../components/MetaTags';
import colors from '../../lib/colors';
import { useDetectedCurrency } from '../../hooks/useDetectedCurrency';
import { makeCurrencyFormatter, type SupportedCurrency } from '../../utils/currency';
import { useFontLoading } from '../../lib/FontContext';
import { fonts, interSemiBold, interMedium, interRegular } from '../../lib/fonts';
import {
    fetchPricingCatalog,
    getStaticPricingCatalog,
    type PricingAccountOffer,
    type PricingAccountProjectContext,
    type PricingCatalog,
    type PricingCatalogPlan,
} from '../../lib/pricingCatalog';
import { localizePricingCatalog } from '../../lib/pricingCatalogCopy';
import {
    ACCOUNT_OFFER_UNAVAILABLE_CODE,
    createCheckoutSession,
    isCheckoutSessionError,
} from '../../lib/checkout';
import { clearCheckoutAttemptId, getCheckoutAttemptId } from '../../lib/checkoutAttempt';
import {
    fetchPersonalOrganizationBillingState,
    type PersonalBillingSubscription,
} from '../../lib/billingCurrency';
import { createBillingPortalSession } from '../../lib/billingPortal';
import { styles } from '../../components/pricing/pricingPageStyles';
import PricingFeatureItem from '../../components/pricing/PricingFeatureItem';
import {
    EarlyUserProjectSummary,
} from '../../components/pricing/EarlyUserProjectOffer';
import { canAccessMainApp } from '../../utils/authHelpers';
import { hasEverLoggedInHint } from '../../utils/firstTimeUser';
import { captureExceptionIfNotNetwork } from '../../lib/sentryUtils';
import { useAppLocale } from '../../lib/i18n/AppI18nProvider';
import {
    resolveMessage,
    type LocalizedMessage,
    useOptionalResolvedMessage,
} from '../../lib/i18n/messages';

type CheckoutIntentParams = {
    checkout_plan?: string | string[];
    checkout_interval?: string | string[];
    checkout_currency?: string | string[];
};

type ProfileWithCurrency = {
    preferred_currency?: SupportedCurrency | null;
    detected_preferred_currency?: SupportedCurrency | null;
};

const CHECKOUT_ERROR_MESSAGE = msg({
    id: 'pricing.error.checkout',
    message: 'We could not start your trial. Refresh this page and try again. If it still fails, email hello@paperzilla.ai and we will help you finish setup.',
});
const OFFER_CHECKOUT_ERROR_MESSAGE = msg({
    id: 'pricing.error.offerCheckout',
    message: 'We could not open checkout. Refresh this page and try again. If it still fails, email mark@paperzilla.ai and we will help you finish setup.',
});
const BILLING_PORTAL_ERROR_MESSAGE = msg({
    id: 'pricing.error.billingPortal',
    message: 'We could not open billing management. Refresh this page and try again. If it still fails, email hello@paperzilla.ai and we will help you finish setup.',
});
const ACTIVE_SUBSCRIPTION_EXISTS_CODE = 'active_subscription_exists';
const OFFER_PROJECT_ERROR_CODES = new Set([
    'selected_plan_project_limit_too_low',
    'project_capacity_unavailable',
]);
const MANAGED_SUBSCRIPTION_STATUSES = ['active', 'trialing', 'past_due', 'unpaid', 'incomplete', 'paused'];

function formatMinorCurrency(
    amountMinorUnits: number,
    currency: SupportedCurrency,
    formatLocale: string,
): string {
    return makeCurrencyFormatter({
        currencyOverride: currency,
        localeTags: [formatLocale],
        formatOptions: {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        },
    }).format(amountMinorUnits / 100);
}

function discountedMinorUnits(amountMinorUnits: number, percentOff: number): number {
    return Math.round(amountMinorUnits * (100 - percentOff) / 100);
}

function formatOfferPercentage(percentOff: number, formatLocale: string): string {
    return new Intl.NumberFormat(formatLocale, {
        maximumFractionDigits: 1,
    }).format(percentOff);
}

function formatPlanNameList(planNames: string[], formatLocale: string): string {
    return new Intl.ListFormat(formatLocale, {
        style: 'long',
        type: 'conjunction',
    }).format(planNames);
}

function formatPlanChoiceList(planNames: string[], formatLocale: string): string {
    return new Intl.ListFormat(formatLocale, {
        style: 'long',
        type: 'disjunction',
    }).format(planNames);
}

function formatOfferDeadline(expiresAt: string | null, formatLocale: string): string | null {
    if (!expiresAt) {
        return null;
    }

    const date = new Date(expiresAt);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return new Intl.DateTimeFormat(formatLocale, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
    }).format(date);
}

function resolveCatalogCurrency(
    catalog: PricingCatalog | null,
    profile: ProfileWithCurrency | null,
    detectedCurrency: SupportedCurrency,
): SupportedCurrency {
    const supportedCurrencies = catalog?.supported_currencies ?? ['USD', 'EUR', 'GBP', 'CNY'];
    const preferredCurrency = profile?.preferred_currency ?? detectedCurrency ?? profile?.detected_preferred_currency;

    return supportedCurrencies.includes(preferredCurrency)
        ? preferredCurrency
        : catalog?.default_currency ?? 'USD';
}

function toSingleParam(value: string | string[] | undefined): string | undefined {
    return Array.isArray(value) ? value[0] : value;
}

function isPublicPlanId(value: string | undefined): value is PricingCatalogPlan['id'] {
    return value === 'starter' || value === 'plus' || value === 'pro';
}

function isPricingInterval(value: string | undefined): value is 'monthly' | 'yearly' {
    return value === 'monthly' || value === 'yearly';
}

function isSupportedCurrency(value: string | undefined): value is SupportedCurrency {
    return value === 'USD' || value === 'EUR' || value === 'GBP' || value === 'CNY';
}

function titleizePlanId(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
}

function isManagedSubscription(subscription: PersonalBillingSubscription | null): boolean {
    return Boolean(subscription && MANAGED_SUBSCRIPTION_STATUSES.includes(subscription.status));
}

function isScheduledCancellation(subscription: PersonalBillingSubscription | null): boolean {
    return Boolean(subscription && (subscription.cancelAtPeriodEnd || subscription.cancelAt));
}

function isCanceledSubscription(subscription: PersonalBillingSubscription | null): boolean {
    return Boolean(subscription && (subscription.status === 'canceled' || isScheduledCancellation(subscription)));
}

function formatDate(value: string | null, formatLocale: string): string | null {
    if (!value) {
        return null;
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    return new Intl.DateTimeFormat(formatLocale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    }).format(date);
}

function checkoutIntentPath(
    planId: PricingCatalogPlan['id'],
    interval: 'monthly' | 'yearly',
    currency: SupportedCurrency,
): string {
    const params = new URLSearchParams({
        checkout_plan: planId,
        checkout_interval: interval,
        checkout_currency: currency,
    });

    return `/pricing?${params.toString()}`;
}

export default function UpgradeScreen() {
    const { i18n } = useLingui();
    const { formatLocale } = useAppLocale();
    const { session, profile, isLoading: isSessionLoading, isReturningUser } = useSession();
    const { openAuth } = useAuth();
    const checkoutIntentParams = useLocalSearchParams<CheckoutIntentParams>();
    const pathname = usePathname();
    const isTrialRoute = pathname === '/trial';
    const isEarlyUserOfferRoute = pathname === '/early-user-offer';
    const { fontsLoaded } = useFontLoading();
    const staticCatalog = useMemo(() => getStaticPricingCatalog(), []);
    const [isAnnual, setIsAnnual] = useState(true);
    const [sourceCatalog, setSourceCatalog] = useState<PricingCatalog | null>(() => staticCatalog);
    const catalog = useMemo(
        () => sourceCatalog ? localizePricingCatalog(i18n, sourceCatalog) : null,
        [i18n, i18n.locale, sourceCatalog],
    );
    const [catalogError, setCatalogError] = useState<LocalizedMessage | null>(null);
    const [isCatalogLoading, setIsCatalogLoading] = useState(!staticCatalog);
    const [hasCheckedLiveCatalog, setHasCheckedLiveCatalog] = useState(false);
    const [offerCheckFailed, setOfferCheckFailed] = useState(false);
    const [offerCheckoutUnavailable, setOfferCheckoutUnavailable] = useState(false);
    const [billingSubscription, setBillingSubscription] = useState<PersonalBillingSubscription | null>(null);
    const [billingCurrency, setBillingCurrency] = useState<SupportedCurrency | null>(null);
    const [billingStateLoadedUserId, setBillingStateLoadedUserId] = useState<string | null>(null);
    const [checkoutPlanId, setCheckoutPlanId] = useState<PricingCatalogPlan['id'] | null>(null);
    const [billingPortalPlanId, setBillingPortalPlanId] = useState<PricingCatalogPlan['id'] | null>(null);
    const [checkoutError, setCheckoutError] = useState<LocalizedMessage | null>(null);
    const resolvedCatalogError = useOptionalResolvedMessage(catalogError);
    const resolvedCheckoutError = useOptionalResolvedMessage(checkoutError);
    const autoCheckoutAttemptRef = useRef<string | null>(null);
    const billingStateRequestIdRef = useRef(0);
    const scrollViewRef = useRef<ScrollViewInstance>(null);
    const detectedCurrency = useDetectedCurrency();
    const profileWithCurrency = profile as ProfileWithCurrency | null;
    const billingCurrencyUserId = canAccessMainApp(session)
        ? session?.user?.id ?? null
        : null;
    const isBillingCurrencyLoading = Boolean(billingCurrencyUserId) && billingStateLoadedUserId !== billingCurrencyUserId;
    const profileCurrency = resolveCatalogCurrency(
        catalog,
        profileWithCurrency,
        detectedCurrency,
    );
    const selectedCurrency = billingCurrency ?? profileCurrency;

    useEffect(() => {
        let isMounted = true;

        async function loadCatalog() {
            setHasCheckedLiveCatalog(false);
            if (!staticCatalog) {
                setIsCatalogLoading(true);
            }
            setCatalogError(null);
            setOfferCheckFailed(false);
            setOfferCheckoutUnavailable(false);

            try {
                const nextCatalog = await fetchPricingCatalog({
                    includeAccountOffer: isEarlyUserOfferRoute,
                });
                if (isMounted) {
                    setSourceCatalog(nextCatalog);
                }
            } catch (error: any) {
                console.error('Error loading pricing catalog:', error);
                if (isEarlyUserOfferRoute) {
                    setOfferCheckFailed(true);
                }
                if (isMounted && !staticCatalog) {
                    setCatalogError(msg({
                        id: 'pricing.error.catalogUnavailable',
                        message: 'Pricing is temporarily unavailable. Please try again shortly.',
                    }));
                }
            } finally {
                if (isMounted) {
                    setIsCatalogLoading(false);
                    setHasCheckedLiveCatalog(true);
                }
            }
        }

        loadCatalog();

        return () => {
            isMounted = false;
        };
    }, [isEarlyUserOfferRoute, staticCatalog]);

    const refreshBillingState = useCallback(async (
        userId: string,
        options?: {
            clearOnError?: boolean;
            showLoading?: boolean;
        },
    ) => {
        const requestId = billingStateRequestIdRef.current + 1;
        billingStateRequestIdRef.current = requestId;

        if (options?.showLoading !== false) {
            setBillingStateLoadedUserId(null);
        }

        try {
            const nextBillingState = await fetchPersonalOrganizationBillingState(userId);
            if (billingStateRequestIdRef.current === requestId) {
                setBillingCurrency(nextBillingState.billingCurrency);
                setBillingSubscription(nextBillingState.subscription);
            }
        } catch (error: any) {
            console.error('Error loading billing currency:', error);
            if (billingStateRequestIdRef.current === requestId && options?.clearOnError !== false) {
                setBillingCurrency(null);
                setBillingSubscription(null);
            }
        } finally {
            if (billingStateRequestIdRef.current === requestId) {
                setBillingStateLoadedUserId(userId);
            }
        }
    }, []);

    useEffect(() => {
        if (!billingCurrencyUserId) {
            billingStateRequestIdRef.current += 1;
            setBillingCurrency(null);
            setBillingSubscription(null);
            setBillingStateLoadedUserId(null);
            return;
        }

        void refreshBillingState(billingCurrencyUserId);
    }, [billingCurrencyUserId, refreshBillingState]);

    useEffect(() => {
        if (!billingCurrencyUserId) {
            return;
        }

        if (Platform.OS !== 'web' || typeof window === 'undefined') {
            return;
        }

        const refreshVisibleBillingState = () => {
            if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
                return;
            }

            void refreshBillingState(billingCurrencyUserId, {
                clearOnError: false,
                showLoading: false,
            });
        };

        window.addEventListener('focus', refreshVisibleBillingState);
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', refreshVisibleBillingState);
        }

        return () => {
            window.removeEventListener('focus', refreshVisibleBillingState);
            if (typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', refreshVisibleBillingState);
            }
        };
    }, [billingCurrencyUserId, refreshBillingState]);

    const checkoutInterval = isAnnual ? 'yearly' : 'monthly';
    const hasManagedBillingSubscription = isManagedSubscription(billingSubscription);
    const hasScheduledBillingCancellation = isScheduledCancellation(billingSubscription);
    const hasCanceledBillingSubscription = isCanceledSubscription(billingSubscription);
    const hasTerminalCanceledBillingSubscription = billingSubscription?.status === 'canceled';
    const shouldUseBillingPortal = hasManagedBillingSubscription && !hasTerminalCanceledBillingSubscription;

    const scrollToPricingTop = useCallback(() => {
        requestAnimationFrame(() => {
            scrollViewRef.current?.scrollTo({ y: 0, animated: true });

            if (Platform.OS === 'web' && typeof window !== 'undefined') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        });
    }, []);

    useEffect(() => {
        const interval = toSingleParam(checkoutIntentParams.checkout_interval);
        if (isPricingInterval(interval)) {
            setIsAnnual(interval === 'yearly');
        }
    }, [checkoutIntentParams.checkout_interval]);

    const startCheckout = useCallback(async (
        plan: PricingCatalogPlan,
        options?: {
            interval?: 'monthly' | 'yearly';
            currency?: SupportedCurrency;
        },
    ) => {
        const interval = options?.interval ?? checkoutInterval;
        const currency = options?.currency ?? selectedCurrency;
        const expectedDiscountGrantId = isEarlyUserOfferRoute
            ? catalog?.account_offer?.id
            : undefined;
        setCheckoutError(null);

        if (isSessionLoading || isBillingCurrencyLoading) {
            return;
        }

        if (billingSubscription) {
            return;
        }

        if (
            isEarlyUserOfferRoute &&
            (
                catalog?.account_offer_status !== 'available' ||
                catalog.account_offer?.billing_mode !== 'immediate' ||
                !expectedDiscountGrantId ||
                !catalog.account_project_context
            )
        ) {
            setCheckoutError(msg({
                id: 'pricing.error.offerNotReady',
                message: 'This early-user offer is not ready for checkout. Refresh this page or email mark@paperzilla.ai.',
            }));
            scrollToPricingTop();
            return;
        }

        const checkoutAttemptSelection = {
            planId: plan.id,
            interval,
            currency,
            expectedDiscountGrantId,
        };
        const checkoutAttemptId = getCheckoutAttemptId(checkoutAttemptSelection);

        if (!canAccessMainApp(session)) {
            const redirectTo = checkoutIntentPath(plan.id, interval, currency);
            const mode: 'signin' | 'signup' = isReturningUser || hasEverLoggedInHint()
                ? 'signin'
                : 'signup';

            openAuth({
                mode,
                redirectTo,
            });
            return;
        }

        setCheckoutPlanId(plan.id);

        try {
            const checkoutSession = await createCheckoutSession({
                lookupKey: plan.checkout_lookup_keys[interval],
                currency,
                checkoutAttemptId,
                expectedDiscountGrantId,
            });
            clearCheckoutAttemptId(checkoutAttemptSelection);

            if (Platform.OS === 'web' && typeof window !== 'undefined') {
                window.location.assign(checkoutSession.url);
                return;
            }

            await Linking.openURL(checkoutSession.url);
        } catch (error: any) {
            console.error('Checkout session creation failed:', error);
            const checkoutSessionError = isCheckoutSessionError(error) ? error : null;
            if (
                checkoutSessionError?.clearAttempt ||
                checkoutSessionError?.code === ACTIVE_SUBSCRIPTION_EXISTS_CODE ||
                checkoutSessionError?.code === ACCOUNT_OFFER_UNAVAILABLE_CODE
            ) {
                clearCheckoutAttemptId(checkoutAttemptSelection);
            }

            if (
                isEarlyUserOfferRoute &&
                checkoutSessionError?.code === ACCOUNT_OFFER_UNAVAILABLE_CODE
            ) {
                setCheckoutError(null);
                setCheckoutPlanId(null);
                setOfferCheckoutUnavailable(true);
                scrollToPricingTop();
                return;
            }

            if (checkoutSessionError?.code === ACTIVE_SUBSCRIPTION_EXISTS_CODE) {
                setCheckoutPlanId(null);

                const userId = session?.user?.id;
                if (userId) {
                    await refreshBillingState(userId);
                }

                setBillingPortalPlanId(plan.id);
                try {
                    const billingPortalUrl = await createBillingPortalSession();

                    if (Platform.OS === 'web' && typeof window !== 'undefined') {
                        window.location.assign(billingPortalUrl);
                        return;
                    }

                    await Linking.openURL(billingPortalUrl);
                    return;
                } catch (billingPortalError: any) {
                    console.error('Billing portal session creation failed after active subscription guard:', billingPortalError);
                    captureExceptionIfNotNetwork(
                        billingPortalError instanceof Error
                            ? billingPortalError
                            : new Error('Billing portal session creation failed after active subscription guard'),
                        {
                            tags: {
                                operation: 'create_billing_portal_session_after_active_subscription_guard',
                                plan_id: plan.id,
                            },
                            extra: {
                                checkoutAttemptId,
                                checkoutErrorCode: checkoutSessionError.code,
                                errorMessage: billingPortalError?.message,
                            },
                        },
                    );
                    setCheckoutError(BILLING_PORTAL_ERROR_MESSAGE);
                    setBillingPortalPlanId(null);
                    scrollToPricingTop();
                    return;
                }
            }

            captureExceptionIfNotNetwork(
                error instanceof Error ? error : new Error('Checkout session creation failed'),
                {
                    tags: {
                        operation: 'create_checkout_session',
                        plan_id: plan.id,
                        checkout_interval: interval,
                    },
                    extra: {
                        currency,
                        lookupKey: plan.checkout_lookup_keys[interval],
                        checkoutAttemptId,
                        checkoutErrorCode: checkoutSessionError?.code,
                        checkoutRetryable: checkoutSessionError?.retryable,
                        errorMessage: error?.message,
                    },
                },
            );
            const projectSelectionMessage: LocalizedMessage | null = checkoutSessionError?.code === 'project_selection_apply_failed'
                ? msg({
                    id: 'pricing.error.projectSelectionApplyFailed',
                    message: 'We could not safely pause the other projects, so Checkout was not opened. Refresh and try again.',
                })
                : checkoutSessionError?.code && OFFER_PROJECT_ERROR_CODES.has(checkoutSessionError.code)
                    ? msg({
                        id: 'pricing.error.projectCapacityUnavailable',
                        message: 'The selected plan cannot safely accommodate your current projects. Choose another plan or email mark@paperzilla.ai.',
                    })
                    : null;
            const displayError = projectSelectionMessage ?? (
                isEarlyUserOfferRoute ? OFFER_CHECKOUT_ERROR_MESSAGE : CHECKOUT_ERROR_MESSAGE
            );
            setCheckoutError(displayError);
            scrollToPricingTop();
            setCheckoutPlanId(null);
        }
    }, [billingSubscription, catalog, checkoutInterval, isBillingCurrencyLoading, isEarlyUserOfferRoute, isReturningUser, isSessionLoading, openAuth, refreshBillingState, scrollToPricingTop, selectedCurrency, session]);

    const openBillingPortalForPlan = useCallback(async (plan: PricingCatalogPlan) => {
        setCheckoutError(null);

        if (isSessionLoading || isBillingCurrencyLoading || !shouldUseBillingPortal) {
            return;
        }

        setBillingPortalPlanId(plan.id);

        try {
            const billingPortalUrl = await createBillingPortalSession();

            if (Platform.OS === 'web' && typeof window !== 'undefined') {
                window.location.assign(billingPortalUrl);
                return;
            }

            await Linking.openURL(billingPortalUrl);
        } catch (error: any) {
            console.error('Billing portal session creation failed:', error);
            captureExceptionIfNotNetwork(
                error instanceof Error ? error : new Error('Billing portal session creation failed'),
                {
                    tags: {
                        operation: 'create_billing_portal_session',
                        plan_id: plan.id,
                    },
                    extra: {
                        currentPlanId: billingSubscription?.planId,
                        currentSubscriptionStatus: billingSubscription?.status,
                        errorMessage: error?.message,
                    },
                },
            );
            setCheckoutError(BILLING_PORTAL_ERROR_MESSAGE);
            setBillingPortalPlanId(null);
            scrollToPricingTop();
        }
    }, [billingSubscription?.planId, billingSubscription?.status, isBillingCurrencyLoading, isSessionLoading, scrollToPricingTop, shouldUseBillingPortal]);

    useEffect(() => {
        if (
            !canAccessMainApp(session) ||
            !catalog ||
            isCatalogLoading ||
            isBillingCurrencyLoading ||
            checkoutPlanId ||
            billingSubscription ||
            (
                isEarlyUserOfferRoute &&
                (
                    catalog.account_offer_status !== 'available' ||
                    catalog.account_offer?.billing_mode !== 'immediate' ||
                    !catalog.account_project_context
                )
            )
        ) {
            return;
        }

        const planId = toSingleParam(checkoutIntentParams.checkout_plan);
        const interval = toSingleParam(checkoutIntentParams.checkout_interval);
        const currency = toSingleParam(checkoutIntentParams.checkout_currency);

        if (!isPublicPlanId(planId) || !isPricingInterval(interval)) {
            return;
        }

        const plan = catalog.plans.find((candidate) => candidate.id === planId);
        if (!plan) {
            return;
        }

        const resolvedCurrency = billingCurrency ?? (
            isSupportedCurrency(currency) ? currency : selectedCurrency
        );
        const attemptKey = `${plan.id}:${interval}:${resolvedCurrency}`;
        if (autoCheckoutAttemptRef.current === attemptKey) {
            return;
        }

        autoCheckoutAttemptRef.current = attemptKey;
        void startCheckout(plan, { interval, currency: resolvedCurrency });
    }, [
        catalog,
        checkoutIntentParams.checkout_currency,
        checkoutIntentParams.checkout_interval,
        checkoutIntentParams.checkout_plan,
        billingCurrency,
        billingSubscription,
        checkoutPlanId,
        isBillingCurrencyLoading,
        isCatalogLoading,
        isEarlyUserOfferRoute,
        selectedCurrency,
        session,
        startCheckout,
    ]);

    const currentSubscriptionPlan = billingSubscription
        ? catalog?.plans.find((candidate) => candidate.id === billingSubscription.planId) ?? null
        : null;
    const currentSubscriptionPlanName = currentSubscriptionPlan?.name ??
        (billingSubscription ? titleizePlanId(billingSubscription.planId) : null);
    const currentSubscriptionEndDate = formatDate(
        billingSubscription?.cancelAt ?? billingSubscription?.currentPeriodEnd ?? null,
        formatLocale,
    );
    const subscriptionNoticeText = billingSubscription
        ? hasCanceledBillingSubscription
            ? hasScheduledBillingCancellation
                ? currentSubscriptionEndDate
                    ? resolveMessage(i18n, msg({
                        id: 'pricing.subscription.scheduledEndOn',
                        message: '{planName} is scheduled to end on {endDate}. Open billing management to re-enable it, or email hello@paperzilla.ai if you need help.',
                    }), {
                        planName: currentSubscriptionPlanName ?? '',
                        endDate: currentSubscriptionEndDate,
                    })
                    : resolveMessage(i18n, msg({
                        id: 'pricing.subscription.scheduledEnd',
                        message: '{planName} is scheduled to end. Open billing management to re-enable it, or email hello@paperzilla.ai if you need help.',
                    }), { planName: currentSubscriptionPlanName ?? '' })
                : resolveMessage(i18n, msg({
                    id: 'pricing.subscription.canceled',
                    message: '{planName} is canceled. Email hello@paperzilla.ai if you want to re-subscribe.',
                }), { planName: currentSubscriptionPlanName ?? '' })
            : billingSubscription.status === 'trialing'
                ? resolveMessage(i18n, msg({
                    id: 'pricing.subscription.currentTrial',
                    message: 'You are currently on the {planName} trial. Use the other plan buttons to change your subscription.',
                }), { planName: currentSubscriptionPlanName ?? '' })
                : resolveMessage(i18n, msg({
                    id: 'pricing.subscription.currentPlan',
                    message: 'You are currently subscribed to {planName}. Use the other plan buttons to change your subscription.',
                }), { planName: currentSubscriptionPlanName ?? '' })
        : null;
    const accountOffer: PricingAccountOffer | null = isEarlyUserOfferRoute &&
        !offerCheckoutUnavailable &&
        catalog?.account_offer_status === 'available'
        ? catalog.account_offer ?? null
        : null;
    const accountProjectContext: PricingAccountProjectContext | null =
        isEarlyUserOfferRoute &&
            !offerCheckoutUnavailable &&
            catalog?.account_offer_status === 'available'
            ? catalog.account_project_context ?? null
            : null;
    const recommendedPlan = accountProjectContext?.recommended_plan_id
        ? catalog?.plans.find((plan) => plan.id === accountProjectContext.recommended_plan_id) ?? null
        : null;
    const isOfferCheckLoading = isEarlyUserOfferRoute && canAccessMainApp(session) && !hasCheckedLiveCatalog;
    const isImmediateAccountOffer = accountOffer?.billing_mode === 'immediate';
    const isOfferCheckUnavailable = isEarlyUserOfferRoute && !isOfferCheckLoading && (
        offerCheckoutUnavailable ||
        offerCheckFailed ||
        catalog?.account_offer_status !== 'available' ||
        !accountOffer ||
        !isImmediateAccountOffer ||
        !accountProjectContext
    );
    const canShowPlanSelection = !isEarlyUserOfferRoute || (
        isImmediateAccountOffer && Boolean(accountProjectContext)
    );
    const offerPercentage = accountOffer ? formatOfferPercentage(accountOffer.percent_off, formatLocale) : null;
    const offerDeadline = accountOffer ? formatOfferDeadline(accountOffer.expires_at, formatLocale) : null;
    const discountedPlanNames = accountOffer
        ? (catalog?.plans ?? [])
            .filter((plan) => accountOffer.discounted_plan_ids.includes(plan.id))
            .map((plan) => plan.name)
        : [];
    const fullPricePlanNames = accountOffer
        ? (catalog?.plans ?? [])
            .filter((plan) => !accountOffer.discounted_plan_ids.includes(plan.id))
            .map((plan) => plan.name)
        : [];
    const discountedPlanLabel = formatPlanNameList(discountedPlanNames, formatLocale);
    const discountedPlanChoiceLabel = formatPlanChoiceList(discountedPlanNames, formatLocale);
    const discountedPlanShortLabel = discountedPlanNames.join('/');
    const fullPricePlanLabel = formatPlanNameList(fullPricePlanNames, formatLocale);
    let pageTitle = isTrialRoute
        ? i18n._(msg({ id: 'pricing.page.freeTrialTitle', message: 'Free Trial' }))
        : i18n._(msg({ id: 'pricing.page.pricingTitle', message: 'Pricing' }));
    if (isImmediateAccountOffer) {
        pageTitle = i18n._(msg({
            id: 'pricing.page.applyEarlyUserDiscountTitle',
            message: 'Apply your early-user discount',
        }));
    } else if (isEarlyUserOfferRoute) {
        pageTitle = i18n._(msg({
            id: 'pricing.page.paperzillaOfferTitle',
            message: 'Your Paperzilla offer',
        }));
    }
    const pageDescription = isImmediateAccountOffer
        ? fullPricePlanNames.length > 0
            ? resolveMessage(i18n, msg({
                id: 'pricing.page.offerDescriptionWithFullPricePlans',
                message: 'Choose {discountedPlans} with {percentage}% off for the continuous life of the subscription, or choose {fullPricePlans} at full price.',
            }), {
                discountedPlans: discountedPlanChoiceLabel,
                percentage: offerPercentage ?? '',
                fullPricePlans: fullPricePlanLabel,
            })
            : resolveMessage(i18n, msg({
                id: 'pricing.page.offerDescription',
                message: 'Choose {discountedPlans} with {percentage}% off for the continuous life of the subscription.',
            }), {
                discountedPlans: discountedPlanChoiceLabel,
                percentage: offerPercentage ?? '',
            })
        : isTrialRoute
            ? i18n._(msg({
                id: 'pricing.page.trialDescription',
                message: 'Choose the plan you want and start with a 14-day free trial. We will remind you before the trial ends, and you can cancel anytime.',
            }))
            : i18n._(msg({
                id: 'pricing.page.description',
                message: 'Choose the right plan for your research needs. From a Starter project to Plus with advanced AI summaries and team collaboration features.',
            }));
    const renderPricingCard = (plan: PricingCatalogPlan) => {
        const interval = checkoutInterval;
        const isRecommendedForProjects = accountProjectContext?.recommended_plan_id === plan.id;
        const discountedPlanIds = accountOffer?.discounted_plan_ids ?? [];
        const isDiscountedByOffer = Boolean(accountOffer && discountedPlanIds.includes(plan.id));
        const planImpact = accountProjectContext?.plan_impacts[plan.id] ?? null;
        const keptProjectNames = (planImpact?.kept_project_ids ?? [])
            .map((projectId) => accountProjectContext?.active_projects.find((project) => project.id === projectId)?.name)
            .filter((name): name is string => Boolean(name));
        const pausedProjectNames = (planImpact?.paused_project_ids ?? [])
            .map((projectId) => accountProjectContext?.active_projects.find((project) => project.id === projectId)?.name)
            .filter((name): name is string => Boolean(name));
        const madePublicProjectNames = (planImpact?.made_public_project_ids ?? [])
            .map((projectId) => accountProjectContext?.active_projects.find((project) => project.id === projectId)?.name)
            .filter((name): name is string => Boolean(name));
        const newlyPausedCount = planImpact?.paused_project_ids.length ?? 0;
        const madePublicCount = planImpact?.made_public_project_ids.length ?? 0;
        const alreadyPausedCount = accountProjectContext?.paused_project_count ?? 0;
        const selectedPrice = plan.prices[interval];
        const regularDisplayPrice = formatMinorCurrency(
            selectedPrice.display_monthly_minor_units[selectedCurrency],
            selectedCurrency,
            formatLocale,
        );
        const regularBilledAmount = formatMinorCurrency(
            selectedPrice.amount_minor_units[selectedCurrency],
            selectedCurrency,
            formatLocale,
        );
        const displayPrice = isDiscountedByOffer
            ? formatMinorCurrency(
                discountedMinorUnits(
                    selectedPrice.display_monthly_minor_units[selectedCurrency],
                    accountOffer?.percent_off ?? 0,
                ),
                selectedCurrency,
                formatLocale,
            )
            : regularDisplayPrice;
        const billedAmount = isDiscountedByOffer
            ? formatMinorCurrency(
                discountedMinorUnits(
                    selectedPrice.amount_minor_units[selectedCurrency],
                    accountOffer?.percent_off ?? 0,
                ),
                selectedCurrency,
                formatLocale,
            )
            : regularBilledAmount;
        const taxNote = catalog?.copy.tax_note ?? '+ applicable tax';
        const priceFootnote = isDiscountedByOffer
            ? isAnnual
                ? resolveMessage(i18n, msg({
                    id: 'pricing.price.discountedAnnualFootnote',
                    message: 'Charged {billedAmount} today, then annually {taxNote} (normally {regularAmount})',
                }), { billedAmount, taxNote, regularAmount: regularBilledAmount })
                : resolveMessage(i18n, msg({
                    id: 'pricing.price.discountedMonthlyFootnote',
                    message: 'Charged {billedAmount} today, then monthly {taxNote} (normally {regularAmount})',
                }), { billedAmount, taxNote, regularAmount: regularBilledAmount })
            : accountOffer
                ? isAnnual
                    ? resolveMessage(i18n, msg({
                        id: 'pricing.price.offerAnnualFootnote',
                        message: 'Charged {amount} today, then annually {taxNote}',
                    }), { amount: regularBilledAmount, taxNote })
                    : resolveMessage(i18n, msg({
                        id: 'pricing.price.offerMonthlyFootnote',
                        message: 'Charged {amount} today, then monthly {taxNote}',
                    }), { amount: regularBilledAmount, taxNote })
                : isAnnual
                    ? resolveMessage(i18n, msg({
                        id: 'pricing.price.annualFootnote',
                        message: 'Billed annually at {amount} {taxNote}',
                    }), { amount: billedAmount, taxNote })
                    : resolveMessage(i18n, msg({
                        id: 'pricing.price.monthlyFootnote',
                        message: 'per month {taxNote}',
                    }), { taxNote });
        const isStartingCheckout = checkoutPlanId === plan.id;
        const isOpeningBillingPortal = billingPortalPlanId === plan.id;
        const isCurrentSubscriptionPlan = billingSubscription?.planId === plan.id;
        const isBillingActionDisabled = isSessionLoading ||
            isBillingCurrencyLoading ||
            Boolean(checkoutPlanId) ||
            Boolean(billingPortalPlanId);
        const currentPlanOrder = currentSubscriptionPlan?.display_order;
        const isDowngradePlan = currentPlanOrder !== undefined && plan.display_order < currentPlanOrder;
        const planChangeLabel = isDowngradePlan
            ? i18n._(msg({ id: 'pricing.action.downgrade', message: 'Downgrade' }))
            : i18n._(msg({ id: 'pricing.action.upgrade', message: 'Upgrade' }));
        const checkoutButtonLabel = isSessionLoading
            ? i18n._(msg({ id: 'pricing.action.checkingAccount', message: 'Checking account...' }))
            : isBillingCurrencyLoading
                ? i18n._(msg({ id: 'pricing.action.checkingBilling', message: 'Checking billing...' }))
                : isOpeningBillingPortal
                    ? i18n._(msg({ id: 'pricing.action.openingBilling', message: 'Opening billing...' }))
                : isStartingCheckout
                ? i18n._(msg({ id: 'pricing.action.startingCheckout', message: 'Starting Checkout...' }))
                : accountOffer
                    ? isDiscountedByOffer
                        ? resolveMessage(i18n, msg({
                            id: 'pricing.action.continueWithPlan',
                            message: 'Continue with {planName}',
                        }), { planName: plan.name })
                        : resolveMessage(i18n, msg({
                            id: 'pricing.action.continueWithPlanFullPrice',
                            message: 'Continue with {planName} at full price',
                        }), { planName: plan.name })
                    : i18n._(msg({ id: 'pricing.action.startTrial', message: 'Start 14-day Trial' }));
        const planStatusLabel = isCurrentSubscriptionPlan
            ? hasCanceledBillingSubscription
                ? i18n._(msg({ id: 'pricing.status.canceled', message: 'Canceled' }))
                : billingSubscription?.status === 'trialing'
                    ? i18n._(msg({ id: 'pricing.status.currentTrial', message: 'Current trial' }))
                    : i18n._(msg({ id: 'pricing.status.currentPlan', message: 'Current plan' }))
            : null;
        const currentPlanText = hasScheduledBillingCancellation && currentSubscriptionEndDate
                ? resolveMessage(i18n, msg({
                    id: 'pricing.subscription.accessEndsOn',
                    message: 'Access ends on {endDate}.',
                }), { endDate: currentSubscriptionEndDate })
                : hasCanceledBillingSubscription
                    ? i18n._(msg({
                        id: 'pricing.subscription.resubscribeHelp',
                        message: 'Email hello@paperzilla.ai if you want to re-subscribe.',
                    }))
                    : i18n._(msg({
                        id: 'pricing.subscription.currentSubscription',
                        message: 'This is your current subscription.',
                    }));
        const topBadgeLabel = planStatusLabel ?? (
            isRecommendedForProjects
                ? alreadyPausedCount > 0
                    ? i18n._(msg({ id: 'pricing.badge.fitsActiveProjects', message: 'Fits your active projects' }))
                    : i18n._(msg({ id: 'pricing.badge.fitsProjects', message: 'Fits your projects' }))
                : plan.badge
        );
        const offerImpactCopy = accountOffer ? [
            keptProjectNames.length > 0
                ? resolveMessage(i18n, msg({
                    id: 'pricing.offer.keptProjectsImpact',
                    message: '{projectCount, plural, one {{projectNames} remains active.} other {{projectNames} remain active.}}',
                }), {
                    projectCount: keptProjectNames.length,
                    projectNames: keptProjectNames.join(', '),
                })
                : null,
            newlyPausedCount > 0
                ? resolveMessage(i18n, msg({
                    id: 'pricing.offer.pausedProjectsImpact',
                    message: '{projectCount, plural, one {{projectLabel} pauses without being deleted.} other {{projectLabel} pause without being deleted.}}',
                }), {
                    projectCount: newlyPausedCount,
                    projectLabel: pausedProjectNames.length === newlyPausedCount
                        ? pausedProjectNames.join(', ')
                        : resolveMessage(i18n, msg({
                            id: 'pricing.offer.projectCount',
                            message: '{projectCount, plural, one {# project} other {# projects}}',
                        }), { projectCount: newlyPausedCount }),
                })
                : null,
            madePublicCount > 0
                ? resolveMessage(i18n, msg({
                    id: 'pricing.offer.publicProjectsImpact',
                    message: '{projectCount, plural, one {{projectLabel} becomes public.} other {{projectLabel} become public.}}',
                }), {
                    projectCount: madePublicCount,
                    projectLabel: madePublicProjectNames.length === madePublicCount
                        ? madePublicProjectNames.join(', ')
                        : resolveMessage(i18n, msg({
                            id: 'pricing.offer.keptProjectCount',
                            message: '{projectCount, plural, one {The kept project} other {The kept projects}}',
                        }), { projectCount: madePublicCount }),
                })
                : null,
            alreadyPausedCount > 0
                ? resolveMessage(i18n, msg({
                    id: 'pricing.offer.alreadyPausedProjectsImpact',
                    message: '{projectCount, plural, one {# already-paused project stays paused until you resume it manually, subject to your plan limit.} other {# already-paused projects stay paused until you resume them manually, subject to your plan limit.}}',
                }), { projectCount: alreadyPausedCount })
                : null,
            isDiscountedByOffer
                ? i18n._(msg({
                    id: 'pricing.offer.discountedChargeImpact',
                    message: 'Your subscription starts when you confirm payment in Stripe. The discounted price is charged immediately.',
                }))
                : resolveMessage(i18n, msg({
                    id: 'pricing.offer.fullPriceChargeImpact',
                    message: '{planName} is charged immediately at full price. Your unused {discountedPlanLabel} discount is not consumed.',
                }), {
                    planName: plan.name,
                    discountedPlanLabel: discountedPlanShortLabel,
                }),
        ].filter((sentence): sentence is string => Boolean(sentence)).join(' ') : null;

        return (
            <View
                key={plan.id}
                style={[
                    styles.pricingCard,
                    plan.badge && styles.popularCard,
                    isRecommendedForProjects && styles.currentPlanCard,
                    isCurrentSubscriptionPlan && styles.currentPlanCard,
                    isCurrentSubscriptionPlan && hasCanceledBillingSubscription && styles.canceledPlanCard,
                ]}
            >
                {topBadgeLabel ? (
                    <View style={[
                        styles.popularBadge,
                        isRecommendedForProjects && styles.currentPlanBadge,
                        planStatusLabel && styles.currentPlanBadge,
                        planStatusLabel && hasCanceledBillingSubscription && styles.canceledPlanBadge,
                    ]}>
                        <Text style={[styles.popularBadgeText, fontsLoaded && fonts.label]}>
                            {topBadgeLabel}
                        </Text>
                    </View>
                ) : null}

                <Text style={[styles.planTitle, fontsLoaded && interSemiBold]}>{plan.name}</Text>

                {isDiscountedByOffer ? (
                    <Text
                        testID={`pricing-regular-price-${plan.id}`}
                        style={[styles.regularPrice, fontsLoaded && interRegular]}
                    >
                        {resolveMessage(i18n, msg({
                            id: 'pricing.price.normalMonthly',
                            message: 'Normally {amount}/mo',
                        }), { amount: regularDisplayPrice })}
                    </Text>
                ) : null}

                {accountOffer && !isDiscountedByOffer ? (
                    <Text
                        testID={`pricing-full-price-${plan.id}`}
                        style={[styles.regularPrice, fontsLoaded && interRegular]}
                    >
                        <Trans id="pricing.price.fullPriceOfferExcluded">
                            Full price — early-user discount does not apply
                        </Trans>
                    </Text>
                ) : null}

                <View style={styles.priceContainer}>
                    <Text
                        testID={isDiscountedByOffer ? `pricing-discounted-price-${plan.id}` : `pricing-regular-current-price-${plan.id}`}
                        style={[styles.price, isDiscountedByOffer && styles.offerPrice, fontsLoaded && interSemiBold]}
                    >
                        {displayPrice}
                    </Text>
                    <Text style={[styles.priceUnit, fontsLoaded && interRegular]}>
                        <Trans id="pricing.price.perMonthShort">/mo</Trans>
                    </Text>
                </View>
                <Text style={[styles.billingText, fontsLoaded && interRegular]}>{priceFootnote}</Text>

                <View style={styles.featureList}>
                    <View style={styles.featureItem}>
                        <Text style={styles.checkmark}>✓</Text>
                        <View style={[styles.featureContent, styles.featureLine]}>
                            <Link
                                href="/research-briefs"
                                target="_blank"
                                testID={`pricing-research-brief-link-${plan.id}`}
                                style={[styles.featureText, styles.featureLink, fontsLoaded && interRegular]}
                            >
                                <Trans id="pricing.feature.morningResearchBrief" comment="Link on every pricing plan to the research briefs page. Opens in a new window.">
                                    Morning research brief in your AI assistant ↗
                                </Trans>
                            </Link>
                        </View>
                    </View>
                    {plan.features.map((feature) => (
                        <PricingFeatureItem
                            key={feature.id}
                            feature={feature}
                            fontsLoaded={fontsLoaded}
                        />
                    ))}
                </View>

                <View style={styles.cardFooter}>
                    {isCurrentSubscriptionPlan || hasCanceledBillingSubscription ? (
                        <>
                            <Text style={[styles.currentPlanText, fontsLoaded && interRegular]}>
                                {isCurrentSubscriptionPlan
                                    ? currentPlanText
                                    : i18n._(msg({
                                        id: 'pricing.subscription.planChangesPaused',
                                        message: 'Plan changes are paused for this account.',
                                    }))}
                            </Text>

                            {isCurrentSubscriptionPlan && shouldUseBillingPortal ? (
                                <TouchableOpacity
                                    style={[styles.secondaryBillingButton, isBillingActionDisabled && styles.ctaButtonDisabled]}
                                    onPress={() => openBillingPortalForPlan(plan)}
                                    disabled={isBillingActionDisabled}
                                >
                                    <Text style={[styles.secondaryBillingButtonText, fontsLoaded && fonts.button]}>
                                        {isOpeningBillingPortal
                                            ? i18n._(msg({
                                                id: 'pricing.action.openingBilling',
                                                message: 'Opening billing...',
                                            }))
                                            : hasScheduledBillingCancellation
                                                ? i18n._(msg({
                                                    id: 'pricing.action.manageSubscription',
                                                    message: 'Manage subscription',
                                                }))
                                                : i18n._(msg({
                                                    id: 'pricing.action.cancelSubscription',
                                                    message: 'Cancel subscription',
                                                }))}
                                    </Text>
                                </TouchableOpacity>
                            ) : null}
                        </>
                    ) : shouldUseBillingPortal ? (
                        isDowngradePlan ? (
                            <Link href="mailto:hello@paperzilla.ai" asChild>
                                <TouchableOpacity style={styles.secondaryBillingButton}>
                                    <Text style={[styles.secondaryBillingButtonText, fontsLoaded && fonts.button]}>
                                        <Trans id="pricing.action.contactToDowngrade">
                                            Contact us to downgrade
                                        </Trans>
                                    </Text>
                                </TouchableOpacity>
                            </Link>
                        ) : (
                            <>
                                <TouchableOpacity
                                    style={[styles.ctaButton, isBillingActionDisabled && styles.ctaButtonDisabled]}
                                    onPress={() => openBillingPortalForPlan(plan)}
                                    disabled={isBillingActionDisabled}
                                >
                                    <Text style={[styles.ctaButtonText, fontsLoaded && fonts.button]}>
                                        {isOpeningBillingPortal ? checkoutButtonLabel : planChangeLabel}
                                    </Text>
                                </TouchableOpacity>

                                <Text style={[styles.disclaimer, fontsLoaded && interRegular]}>
                                    <Trans id="pricing.disclosure.opensStripeBilling">
                                        Opens secure Stripe billing management.
                                    </Trans>
                                </Text>
                            </>
                        )
                    ) : (
                        <>
                            <TouchableOpacity
                                style={[styles.ctaButton, isBillingActionDisabled && styles.ctaButtonDisabled]}
                                onPress={() => void startCheckout(plan)}
                                disabled={isBillingActionDisabled}
                            >
                                <Text style={[styles.ctaButtonText, fontsLoaded && fonts.button]}>{checkoutButtonLabel}</Text>
                            </TouchableOpacity>

                            <Text style={[styles.disclaimer, fontsLoaded && interRegular]}>
                                {offerImpactCopy ?? i18n._(msg({
                                    id: 'pricing.disclosure.secureCheckoutTrial',
                                    message: 'Secure Stripe Checkout. Cancel anytime before your trial ends; no charge until then.',
                                }))}
                            </Text>
                        </>
                    )}
                </View>
            </View>
        );
    };

    return (
        <>
            <MetaTags
                surfaceId={isEarlyUserOfferRoute
                    ? 'early-user-offer'
                    : isTrialRoute
                        ? 'trial'
                        : 'pricing'}
                title={accountOffer
                    ? msg({
                        id: 'pricing.seo.earlyUserOfferTitle',
                        message: 'Paperzilla Early-User Offer',
                    })
                    : isTrialRoute
                        ? msg({
                            id: 'pricing.seo.freeTrialTitle',
                            message: 'Paperzilla Free Trial',
                        })
                        : msg({
                            id: 'pricing.seo.pricingTitle',
                            message: 'Paperzilla Pricing',
                        })}
                description={pageDescription}
            />
            <Stack.Screen options={{ headerShown: false }} />
            <KeyboardAvoidingView
                style={styles.container}
                behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
                keyboardVerticalOffset={Platform.OS === 'ios' ? 120 : 40}
            >
                <Header />
                <ScrollView
                    ref={scrollViewRef}
                    style={styles.scrollView}
                    showsVerticalScrollIndicator={false}
                    keyboardShouldPersistTaps="handled"
                >
                    <View style={styles.content}>
                        <Link href="/" asChild>
                            <TouchableOpacity style={styles.backLink}>
                                <Text style={[styles.backLinkText, fontsLoaded && interMedium]}>
                                    <Trans id="pricing.backToHome">← Back to home</Trans>
                                </Text>
                            </TouchableOpacity>
                        </Link>

                        <Text
                            style={[styles.title, (isTrialRoute || isEarlyUserOfferRoute) && styles.titleWithSubtitle, fontsLoaded && fonts.displayMedium]}
                            accessibilityRole="header"
                            {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 1 })}
                        >
                            {pageTitle}
                        </Text>

                        {(isTrialRoute || isImmediateAccountOffer) && !isOfferCheckLoading ? (
                            <Text style={[styles.subtitle, fontsLoaded && interRegular]}>
                                {pageDescription}
                            </Text>
                        ) : null}

                        {accountOffer ? (
                            <View testID="legacy-offer-banner" style={styles.offerBanner}>
                                <Text style={[styles.offerEyebrow, fontsLoaded && fonts.label]}>
                                    <Trans id="pricing.offer.eyebrow">EARLY-USER OFFER</Trans>
                                </Text>
                                <Text style={[styles.offerTitle, fontsLoaded && interSemiBold]}>
                                    {resolveMessage(i18n, msg({
                                        id: 'pricing.offer.title',
                                        message: '{percentage}% off {planNames} for life',
                                    }), {
                                        percentage: offerPercentage ?? '',
                                        planNames: discountedPlanLabel,
                                    })}
                                </Text>
                                <Text style={[styles.offerDescription, fontsLoaded && interRegular]}>
                                    {offerDeadline
                                        ? fullPricePlanNames.length > 0
                                            ? resolveMessage(i18n, msg({
                                                id: 'pricing.offer.deadlineDescriptionWithFullPricePlans',
                                                message: 'Claim before {deadline}. The {discountedPlans} discount is applied automatically at checkout and lasts while the resulting subscription stays continuously active. {fullPricePlanCount, plural, one {{fullPricePlans} remains full price.} other {{fullPricePlans} remain full price.}}',
                                            }), {
                                                deadline: offerDeadline,
                                                discountedPlans: discountedPlanShortLabel,
                                                fullPricePlanCount: fullPricePlanNames.length,
                                                fullPricePlans: fullPricePlanLabel,
                                            })
                                            : resolveMessage(i18n, msg({
                                                id: 'pricing.offer.deadlineDescription',
                                                message: 'Claim before {deadline}. The {discountedPlans} discount is applied automatically at checkout and lasts while the resulting subscription stays continuously active.',
                                            }), {
                                                deadline: offerDeadline,
                                                discountedPlans: discountedPlanShortLabel,
                                            })
                                        : fullPricePlanNames.length > 0
                                            ? resolveMessage(i18n, msg({
                                                id: 'pricing.offer.descriptionWithFullPricePlans',
                                                message: 'The {discountedPlans} discount lasts while the resulting subscription stays continuously active and is applied automatically. {fullPricePlanCount, plural, one {{fullPricePlans} remains full price.} other {{fullPricePlans} remain full price.}}',
                                            }), {
                                                discountedPlans: discountedPlanShortLabel,
                                                fullPricePlanCount: fullPricePlanNames.length,
                                                fullPricePlans: fullPricePlanLabel,
                                            })
                                            : resolveMessage(i18n, msg({
                                                id: 'pricing.offer.description',
                                                message: 'The {discountedPlans} discount lasts while the resulting subscription stays continuously active and is applied automatically.',
                                            }), { discountedPlans: discountedPlanShortLabel })}
                                </Text>
                            </View>
                        ) : null}

                        {accountOffer && accountProjectContext ? (
                            <EarlyUserProjectSummary
                                context={accountProjectContext}
                                recommendedPlan={recommendedPlan}
                                fontsLoaded={fontsLoaded}
                            />
                        ) : null}

                        {isOfferCheckLoading ? (
                            <View style={styles.catalogNotice}>
                                <Text style={[styles.catalogNoticeText, fontsLoaded && interRegular]}>
                                    <Trans id="pricing.offer.checking">Checking your early-user offer...</Trans>
                                </Text>
                            </View>
                        ) : null}

                        {isOfferCheckUnavailable ? (
                            <View style={[styles.catalogNotice, styles.offerUnavailableNotice]}>
                                <Text style={[styles.catalogNoticeText, fontsLoaded && interRegular]}>
                                    {offerCheckoutUnavailable
                                        ? i18n._(msg({
                                            id: 'pricing.offer.noLongerAvailable',
                                            message: 'This early-user offer is no longer available. View the current plans to continue at normal pricing.',
                                        }))
                                        : accountOffer && !isImmediateAccountOffer
                                        ? i18n._(msg({
                                            id: 'pricing.offer.notReadyForBilling',
                                            message: 'This account offer is not ready for immediate billing. Email mark@paperzilla.ai before subscribing.',
                                        }))
                                        : catalog?.account_offer_status === 'none'
                                            ? i18n._(msg({
                                                id: 'pricing.offer.wrongAccount',
                                                message: 'This early-user offer is not available for this account. Sign in with the address that received the offer, or email mark@paperzilla.ai.',
                                            }))
                                            : i18n._(msg({
                                                id: 'pricing.offer.verificationFailed',
                                                message: 'We could not verify your account offer. Refresh this page or email mark@paperzilla.ai before subscribing.',
                                            }))}
                                </Text>
                                <Link href="/pricing" asChild>
                                    <TouchableOpacity
                                        testID="early-user-offer-view-normal-pricing"
                                        style={styles.secondaryBillingButton}
                                    >
                                        <Text style={[styles.secondaryBillingButtonText, fontsLoaded && fonts.button]}>
                                            <Trans id="pricing.offer.viewCurrentPlans">View current plans</Trans>
                                        </Text>
                                    </TouchableOpacity>
                                </Link>
                            </View>
                        ) : null}

                        {/* Billing Toggle */}
                        {canShowPlanSelection && !isOfferCheckLoading ? <View style={styles.toggleContainer}>
                            <TouchableOpacity
                                onPress={() => setIsAnnual(false)}
                                style={styles.toggleLabelContainer}
                            >
                                <Text style={[styles.toggleLabel, !isAnnual && styles.toggleLabelActive, fontsLoaded && interMedium]}>
                                    <Trans id="pricing.billing.monthly">Monthly</Trans>
                                </Text>
                            </TouchableOpacity>

                            <AnnualBillingSwitch
                                value={isAnnual}
                                onValueChange={setIsAnnual}
                            />

                            <TouchableOpacity
                                onPress={() => setIsAnnual(true)}
                                style={styles.toggleLabelContainer}
                            >
                                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                                    <Text style={[styles.toggleLabel, isAnnual && styles.toggleLabelActive, fontsLoaded && interMedium]}>
                                        <Trans id="pricing.billing.annual">Annual</Trans>
                                    </Text>
                                    <View style={styles.saveBadge}>
                                        <Text style={[styles.saveBadgeText, fontsLoaded && fonts.label]}>
                                            {catalog?.copy.annual_savings_label ?? i18n._(msg({
                                                id: 'pricing.catalog.annualSavingsLabel',
                                                message: 'Save ~20%',
                                            }))}
                                        </Text>
                                    </View>
                                </View>
                            </TouchableOpacity>
                        </View> : null}

                        {/* Pricing Cards */}
                        {resolvedCatalogError ? (
                            <View style={styles.catalogNotice}>
                                <Text style={[styles.catalogNoticeText, fontsLoaded && interRegular]}>
                                    {resolvedCatalogError}
                                </Text>
                            </View>
                        ) : null}

                        {resolvedCheckoutError ? (
                            <View style={[styles.catalogNotice, styles.checkoutErrorNotice]}>
                                <Text style={[styles.catalogNoticeText, styles.checkoutErrorText, fontsLoaded && interRegular]}>
                                    {resolvedCheckoutError}
                                </Text>
                            </View>
                        ) : null}

                        {subscriptionNoticeText ? (
                            <View style={[
                                styles.catalogNotice,
                                styles.subscriptionNotice,
                                hasCanceledBillingSubscription && styles.subscriptionCanceledNotice,
                            ]}>
                                <Text style={[
                                    styles.catalogNoticeText,
                                    styles.subscriptionNoticeText,
                                    hasCanceledBillingSubscription && styles.subscriptionCanceledNoticeText,
                                    fontsLoaded && interRegular,
                                ]}>
                                    {subscriptionNoticeText}
                                </Text>
                            </View>
                        ) : null}

                        {isCatalogLoading ? (
                            <View style={styles.catalogNotice}>
                                <Text style={[styles.catalogNoticeText, fontsLoaded && interRegular]}>
                                    <Trans id="pricing.loadingPlans">Loading plans...</Trans>
                                </Text>
                            </View>
                        ) : null}

                        {catalog && canShowPlanSelection && !isOfferCheckLoading ? (
                            <View style={styles.pricingGrid}>
                                {catalog.plans.map(renderPricingCard)}
                            </View>
                        ) : null}

                        {/* Institution and Enterprise Cards */}
                        {canShowPlanSelection ? <View style={styles.slimCardsContainer}>
                            {/* Institution */}
                            <View style={styles.slimCard}>
                                <View style={styles.slimCardContent}>
                                    <View style={styles.slimIconBox}>
                                        <FontAwesome6 name="graduation-cap" size={24} color={colors.text700} />
                                    </View>
                                    <View style={styles.slimCardRight}>
                                        <Text style={[styles.slimCardTitle, fontsLoaded && interSemiBold]}>
                                            <Trans id="pricing.institution.title">Institution</Trans>
                                        </Text>
                                        <Text style={[styles.slimCardSubtitle, fontsLoaded && interRegular]}>
                                            <Trans id="pricing.institution.subtitle">
                                                Campus-wide discovery with librarian superpowers
                                            </Trans>
                                        </Text>
                                        <View style={styles.slimFeatureGrid}>
                                            <View style={styles.slimFeatureItem}>
                                                <Text style={styles.slimCheckmark}>✓</Text>
                                                <Text style={[styles.slimFeatureText, fontsLoaded && interRegular]}>
                                                    <Trans id="pricing.institution.departmentWorkspaces">Department workspaces &amp; roles</Trans>
                                                </Text>
                                            </View>
                                            <View style={styles.slimFeatureItem}>
                                                <Text style={styles.slimCheckmark}>✓</Text>
                                                <Text style={[styles.slimFeatureText, fontsLoaded && interRegular]}>
                                                    <Trans id="pricing.institution.identity">SSO (edu SAML), ORCID, link-resolvers</Trans>
                                                </Text>
                                            </View>
                                            <View style={styles.slimFeatureItem}>
                                                <Text style={styles.slimCheckmark}>✓</Text>
                                                <Text style={[styles.slimFeatureText, fontsLoaded && interRegular]}>
                                                    <Trans id="pricing.institution.analytics">Usage analytics for librarians / PIs</Trans>
                                                </Text>
                                            </View>
                                            <View style={styles.slimFeatureItem}>
                                                <Text style={styles.slimCheckmark}>✓</Text>
                                                <Text style={[styles.slimFeatureText, fontsLoaded && interRegular]}>
                                                    <Trans id="pricing.institution.licenses">Site/department licenses &amp; invoicing</Trans>
                                                </Text>
                                            </View>
                                        </View>
                                        <Link href="mailto:hello@paperzilla.ai" asChild>
                                            <TouchableOpacity style={styles.slimButton}>
                                                <Text style={[styles.slimButtonText, fontsLoaded && fonts.button]}>
                                                    <Trans id="pricing.action.contactUs">Contact us</Trans>
                                                </Text>
                                            </TouchableOpacity>
                                        </Link>
                                    </View>
                                </View>
                            </View>

                            {/* Enterprise */}
                            <View style={styles.slimCard}>
                                <View style={styles.slimCardContent}>
                                    <View style={styles.slimIconBox}>
                                        <FontAwesome6 name="building" size={24} color={colors.text700} />
                                    </View>
                                    <View style={styles.slimCardRight}>
                                        <Text style={[styles.slimCardTitle, fontsLoaded && interSemiBold]}>
                                            <Trans id="pricing.enterprise.title">Enterprise</Trans>
                                        </Text>
                                        <Text style={[styles.slimCardSubtitle, fontsLoaded && interRegular]}>
                                            <Trans id="pricing.enterprise.subtitle">
                                                R&amp;D-grade search with security that won't block you
                                            </Trans>
                                        </Text>
                                        <View style={styles.slimFeatureGrid}>
                                            <View style={styles.slimFeatureItem}>
                                                <Text style={styles.slimCheckmark}>✓</Text>
                                                <Text style={[styles.slimFeatureText, fontsLoaded && interRegular]}>
                                                    <Trans id="pricing.enterprise.identity">SSO/SAML, SCIM, audit logs, DLP</Trans>
                                                </Text>
                                            </View>
                                            <View style={styles.slimFeatureItem}>
                                                <Text style={styles.slimCheckmark}>✓</Text>
                                                <Text style={[styles.slimFeatureText, fontsLoaded && interRegular]}>
                                                    <Trans id="pricing.enterprise.compliance">SOC 2, data residency options</Trans>
                                                </Text>
                                            </View>
                                            <View style={styles.slimFeatureItem}>
                                                <Text style={styles.slimCheckmark}>✓</Text>
                                                <Text style={[styles.slimFeatureText, fontsLoaded && interRegular]}>
                                                    <Trans id="pricing.enterprise.privateModels">Private model endpoints / VPC routing</Trans>
                                                </Text>
                                            </View>
                                            <View style={styles.slimFeatureItem}>
                                                <Text style={styles.slimCheckmark}>✓</Text>
                                                <Text style={[styles.slimFeatureText, fontsLoaded && interRegular]}>
                                                    <Trans id="pricing.enterprise.customRules">Custom COI rule packs &amp; export APIs</Trans>
                                                </Text>
                                            </View>
                                        </View>
                                        <Link href="mailto:hello@paperzilla.ai" asChild>
                                            <TouchableOpacity style={styles.slimButton}>
                                                <Text style={[styles.slimButtonText, fontsLoaded && fonts.button]}>
                                                    <Trans id="pricing.action.contactUs">Contact us</Trans>
                                                </Text>
                                            </TouchableOpacity>
                                        </Link>
                                    </View>
                                </View>
                            </View>
                        </View> : null}

                        {canShowPlanSelection ? <Text style={[styles.footerDisclaimer, fontsLoaded && interRegular]}>
                            <Trans id="pricing.disclosure.subjectToChange">
                                Pricing and features are subject to change.
                            </Trans>
                        </Text> : null}

                    </View>
                    <Footer />
                </ScrollView>
            </KeyboardAvoidingView>
        </>
    );
}

export function generateMetadata() {
    return staticPageMetadata({
        surfaceId: 'pricing',
        title: msg({ id: 'pricing.seo.pricingTitle', message: 'Paperzilla Pricing' }),
        description: msg({ id: 'pricing.page.description', message: 'Choose the right plan for your research needs. From a Starter project to Plus with advanced AI summaries and team collaboration features.' }),
    });
}
